import type {
  ComputeProvisioningItem,
  ComputeProvisioningPhaseName
} from '@schema';
import {
  ComputeProvisioningPhaseState,
  COMPUTE_PROVISIONING_ITEM_STATE_ICON,
  COMPUTE_PROVISIONING_PHASE_ORDER,
  COMPUTE_PROVISIONING_PHASE_STATE_ICON,
  COMPUTE_PROVISIONING_PHASE_STATE_TEXT,
  COMPUTE_PROVISIONING_PHASE_TEXT
} from '@/common/constants/computeProvisioning.constants';
import type { ComputeProvisioningStatus } from '@/common/store/pinia/computeProvisioning';
import {
  flattenProblems,
  type FlattenedProblem
} from '@/hci/pages/infrastructure/nodes/createComputeClusterWizard/provisioningRetry';

export interface MappedComputeProvisioningItem {
  id: string;
  label: string;
  state: ComputeProvisioningItem['state'];
  statusIcon: string;
  fieldPath: string | null;
}

export interface MappedComputeProvisioningPhase {
  name: ComputeProvisioningPhaseName;
  state: ComputeProvisioningPhaseState;
  phaseText: string;
  statusText: string;
  statusIcon: string;
  completedItems: number;
  totalItems: number;
  progress?: number;
  fieldPath: string | null;
  items: MappedComputeProvisioningItem[];
}

const itemProblemRank = (problem: FlattenedProblem, item: ComputeProvisioningItem): number => {
  const { node_id: nodeId, interface_name: interfaceName } = item.details || {};
  if (nodeId && problem.node_id === nodeId && (!interfaceName || problem.interface_name === interfaceName)) {
    return 0;
  }
  if (!problem.node_id && !problem.interface_name) {
    return 1;
  }
  return -1;
};

const fieldPathForItem = (problems: FlattenedProblem[], item: ComputeProvisioningItem): string | null => {
  const [best] = problems
    .map(problem => ({ problem, rank: itemProblemRank(problem, item) }))
    .filter(entry => entry.rank >= 0)
    .sort((a, b) => a.rank - b.rank);
  return best?.problem.field_path ?? null;
};

const mapItem = (
  item: ComputeProvisioningItem,
  phaseProblems: FlattenedProblem[]
): MappedComputeProvisioningItem => ({
  id: item.id,
  label: item.label,
  state: item.state,
  statusIcon: COMPUTE_PROVISIONING_ITEM_STATE_ICON[item.state],
  fieldPath: fieldPathForItem(phaseProblems, item)
});

type TranslateFn = (key: string, params?: Record<string, unknown>) => string;

export const phaseProgressText = (
  phase: MappedComputeProvisioningPhase,
  t: TranslateFn
): string => {
  if (phase.totalItems > 0) {
    return t('{completed} of {total}', { completed: phase.completedItems, total: phase.totalItems });
  }

  if (typeof phase.progress === 'number' && phase.state === ComputeProvisioningPhaseState.RUNNING) {
    return t('{percent}%', { percent: Math.round(phase.progress) });
  }

  return phase.statusText;
};

export const mapPhases = (
  task?: ComputeProvisioningStatus | null
): MappedComputeProvisioningPhase[] => {
  const problems = flattenProblems(task?.problem ? [task.problem] : []);

  return COMPUTE_PROVISIONING_PHASE_ORDER.map((name) => {
    const reported = task?.phases?.find(phase => phase.name === name);
    const state = (reported?.state as ComputeProvisioningPhaseState) ?? ComputeProvisioningPhaseState.PENDING;
    const phaseProblems = problems.filter(problem => problem.phase === name && problem.field_path);

    return {
      name,
      state,
      phaseText: COMPUTE_PROVISIONING_PHASE_TEXT[name],
      statusText: COMPUTE_PROVISIONING_PHASE_STATE_TEXT[state],
      statusIcon: COMPUTE_PROVISIONING_PHASE_STATE_ICON[state],
      completedItems: reported?.completed_items ?? 0,
      totalItems: reported?.total_items ?? 0,
      progress: reported?.progress,
      fieldPath: phaseProblems[0]?.field_path ?? null,
      items: (reported?.items ?? []).map(item => mapItem(item, phaseProblems))
    };
  });
};

export default mapPhases;
